import { RECEIPT_CHANNEL_LABELS, type ReceiptListQuery } from '@chukua/common';
import type { TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { formatReceiptAmount } from '../policies/format-receipt-amount';
import { receiptPath } from '../../../billing.paths';
import type { ReceiptAction } from '../policies/receipt-actions';
import type { ReceiptView } from '../receipts.api';
import { ReceiptActions } from './ReceiptActions';
import { ReceiptStatusTag } from './ReceiptStatusTag';

interface BuildReceiptColumnsArgs {
  list: Pick<ListQueryControls<ReceiptListQuery>, 'sortOrderFor'>;
  /** Given only to someone with `payment.record`; without it the table has no actions column. */
  onAction?: (action: ReceiptAction, receipt: ReceiptView) => void;
}

/** Sorting runs on the server: a sortable column's `key` is its `sort` field in `receiptListQuerySchema`. */
export function buildReceiptColumns({ list, onAction }: BuildReceiptColumnsArgs): TableColumnsType<ReceiptView> {
  const columns: TableColumnsType<ReceiptView> = [
    {
      title: 'Received on',
      dataIndex: 'receivedOn',
      key: 'receivedOn',
      sorter: true,
      sortOrder: list.sortOrderFor('receivedOn'),
    },
    { title: 'Customer', key: 'customer', render: (_, receipt) => receipt.customer.name },
    { title: 'Channel', key: 'channel', render: (_, receipt) => RECEIPT_CHANNEL_LABELS[receipt.channel] },
    {
      title: 'Status',
      key: 'status',
      sorter: true,
      sortOrder: list.sortOrderFor('status'),
      render: (_, receipt) => <ReceiptStatusTag status={receipt.status} />,
    },
    {
      title: 'Reference',
      key: 'reference',
      render: (_, receipt) => (
        <Link className="font-medium text-link hover:underline" to={receiptPath(receipt.id)}>
          {receipt.reference ?? <span className="text-muted">No reference</span>}
        </Link>
      ),
    },
    {
      title: 'Amount',
      key: 'amountMinor',
      align: 'right',
      sorter: true,
      sortOrder: list.sortOrderFor('amountMinor'),
      render: (_, receipt) => formatReceiptAmount(receipt),
    },
    { title: 'Payer', dataIndex: 'payer', key: 'payer' },
    { title: 'Account', key: 'account', render: (_, receipt) => receipt.companyAccount?.name ?? receipt.bankAccount },
    { title: 'Recorded by', key: 'recordedBy', render: (_, receipt) => receipt.recordedBy.name },
  ];
  if (!onAction) return columns;
  const actions: TableColumnsType<ReceiptView>[number] = {
    title: 'Actions',
    key: 'actions',
    // The table scrolls sideways on narrower screens; pinned, the buttons stay in reach instead of sitting off to the right.
    fixed: 'right',
    render: (_, receipt) => <ReceiptActions receipt={receipt} size="small" onAction={(action) => onAction(action, receipt)} />,
  };
  return [...columns, actions];
}
